import { Breadcrumb } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FileTextOutlined, HomeOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { routeConfigs, matchRoute } from "../../routes/routeConfig";

const safeDecode = (str: string | null | undefined): string => {
  if (!str) return '';
  try {
    return safeDecode(str);
  } catch {
    return str;
  }
};

const BOARD_LABEL_MAP: Record<string, string> = {
  Consultation: "Consultation/Discussion",
};

const getBoardLabel = (board: string | null | undefined): string => {
  if (!board) return '';
  const decoded = safeDecode(board);
  return BOARD_LABEL_MAP[decoded] ?? decoded;
};

type CustomBreadcrumbProps = {};

const CustomBreadcrumb: React.FC<CustomBreadcrumbProps> = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isPostPage = location.pathname.startsWith("/post/");
  const isViewerPage = location.pathname.startsWith("/viewer");
  const isViewerSample2Page = location.pathname.startsWith("/viewersample2");
  // [통합] /consultation -> /post 통합으로 isConsultationPage 불필요 (롤백 시 아래 주석 해제)
  // const isConsultationPage = location.pathname.startsWith("/consultation/");
  const isViewerSlide = location.pathname.startsWith("/viewer/");
  const isDemoViewerPage = location.pathname.startsWith("/demo/viewer");
  const isDemoConsultationPage = location.pathname.startsWith("/demo/consultation");

  // 현재 경로에 매칭되는 config 찾기
  const currentConfig = matchRoute(location.pathname);
  if (!currentConfig?.breadcrumb) {
    return null;
  }

  // 데모 뷰어: 네비게이션 없이 라벨만 표시
  if (isDemoViewerPage) {
    const slideIndex = new URLSearchParams(location.search).get('slideIndex');
    const slideName = new URLSearchParams(location.search).get('slideName');

    const items: any[] = [{ title: <span>Viewer</span> }];

    if (slideIndex) {
      const slideLabel = slideName ? `#${slideIndex} ${decodeURIComponent(slideName)}` : `#${slideIndex}`;
      items.push({ title: <span>{slideLabel}</span> });
    }

    return (
      <BreadcrumbWrapper $isPostPage={false} $isViewerPage={true} $isViewerSample2Page={false}>
        <BreadcrumbStyle
          $isViewerPage={true}
          style={{ padding: "0.2rem 1.2rem", backgroundColor: "#0a1929" }}
          items={items}
        />
      </BreadcrumbWrapper>
    );
  }

  if (isDemoConsultationPage) {
    const searchParams = new URLSearchParams(location.search);
    const categoryName = searchParams.get("categoryName");
    const title = searchParams.get("title");
    const boardId = location.pathname.split("/").pop();
    const items: any[] = [{ title: <span>Consultation</span> }];

    if (categoryName) {
      items.push({ title: <span>{safeDecode(categoryName)}</span> });
    }

    if (title) {
      items.push({ title: <span>{`${safeDecode(title)}${boardId ? `(${boardId})` : ""}`}</span> });
    } else if (boardId) {
      items.push({ title: <span>{`(${boardId})`}</span> });
    }

    return (
      <BreadcrumbWrapper $isPostPage={false} $isViewerPage={false} $isViewerSample2Page={false}>
        <BreadcrumbStyle
          $isViewerPage={false}
          style={{ padding: "0.6rem 1.2rem", backgroundColor: "#fff" }}
          items={items}
        />
      </BreadcrumbWrapper>
    );
  }

  // URL에서 쿼리파라미터 가져오기
  const searchParams = new URLSearchParams(location.search);

  // 현재 페이지의 브레드크럼브 아이템 생성
  const buildBreadcrumbItems = () => {
    if (!currentConfig?.breadcrumb) return [];

    const items: any[] = [];
    const currentBreadcrumb = currentConfig.breadcrumb;

    // 1. Home 아이템 (항상 첫번째)
    items.push({
      title: (
        <Link to="/">
          <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <HomeOutlined />
            <span>Home</span>
          </div>
        </Link>
      ),
    });

    // SOA 데이터 헬퍼 함수
    const getSoaParams = () => {
      const soaParams: any = {};
      const meetGender = searchParams.get('meetGender');
      const meetAge = searchParams.get('meetAge');
      const meetOrgan = searchParams.get('meetOrgan');
      const meetDiagnosis = searchParams.get('meetDiagnosis');
      const meetOrganization = searchParams.get('meetOrganization');

      if (meetGender) soaParams.meetGender = meetGender;
      if (meetAge) soaParams.meetAge = meetAge;
      if (meetOrgan) soaParams.meetOrgan = meetOrgan;
      if (meetDiagnosis) soaParams.meetDiagnosis = meetDiagnosis;
      if (meetOrganization) soaParams.meetOrganization = meetOrganization;

      return soaParams;
    };

    // 관리자페이지에서 접근한 경우
    const from = searchParams.get('from');
    if (from === 'admin' && (isPostPage || isViewerSlide)) {
      const board = searchParams.get('board');
      const boardName = searchParams.get('boardName');
      const main = searchParams.get('main');
      const mainName = searchParams.get('mainName');
      const sub = searchParams.get('sub');
      const subName = searchParams.get('subName');
      const title = searchParams.get('title');

      const adminCategoryParams = new URLSearchParams();
      if (board) adminCategoryParams.set('board', board);
      if (boardName) adminCategoryParams.set('boardName', boardName);
      if (main) adminCategoryParams.set('main', main);
      if (mainName) adminCategoryParams.set('mainName', mainName);
      if (sub) adminCategoryParams.set('sub', sub);
      if (subName) adminCategoryParams.set('subName', subName);

      items.push({
        title: (
          <StyledLink to={`/admin/category?${adminCategoryParams.toString()}`}>
            <span>Category</span>
          </StyledLink>
        ),
      });

      if (!isViewerSlide) {
        const postId = location.pathname.split('/').pop();
        const titleStr = title ? safeDecode(title) : 'Post';
        const displayTitle = postId ? `${titleStr} (${postId})` : titleStr;
        items.push({
          title: (
            <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
              <FileTextOutlined />
              <span>{displayTitle}</span>
            </div>
          ),
        });
      } else {
        const postId = searchParams.get('postId');
        const titleStr = title ? safeDecode(title) : 'Post';
        let effectivePostId = postId;
        if (!effectivePostId) {
          const pathSegments = location.pathname.split('/');
          const slideParam = pathSegments[pathSegments.length - 1];
          if (slideParam && /^\d+$/.test(slideParam)) {
            effectivePostId = slideParam;
          }
        }

        if (effectivePostId) {
          const postParams = new URLSearchParams();
          postParams.set('from', 'admin');
          if (board) postParams.set('board', board);
          if (boardName) postParams.set('boardName', boardName);
          if (main) postParams.set('main', main);
          if (mainName) postParams.set('mainName', mainName);
          if (sub) postParams.set('sub', sub);
          if (subName) postParams.set('subName', subName);
          if (title) postParams.set('title', title);

          // SOA 데이터 추가
          const soaParams = getSoaParams();
          Object.entries(soaParams).forEach(([key, value]) => {
            postParams.set(key, value as string);
          });

          const displayTitle = `${titleStr} (${effectivePostId})`;

          items.push({
            title: (
              <StyledLink to={`/post/${effectivePostId}?${postParams.toString()}`}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                  <FileTextOutlined />
                  <span>{displayTitle}</span>
                </div>
              </StyledLink>
            ),
          });
        } else {
          items.push({
            title: (
              <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                <FileTextOutlined />
                <span>{titleStr}</span>
              </div>
            ),
          });
        }

        // 현재 뷰어 슬라이드
        const slideName = searchParams.get('slideName');
        const slideIndex = searchParams.get('slideIndex');
        let viewerLabel = 'Viewer';
        if (slideIndex) {
          viewerLabel = slideName ? `#${slideIndex} ${safeDecode(slideName)}` : `#${slideIndex}`;
        } else if (slideName) {
          viewerLabel = safeDecode(slideName);
        }

        items.push({
          title: (
            <span>{viewerLabel}</span>
          ),
        });
      }

      return items;
    }

    // 동적 parent 결정 (쿼리파라미터 from으로)
    const getDynamicParent = (config: typeof currentConfig): string | undefined => {
      // 이미 parent가 정의되어 있으면 그대로 사용
      if (config?.breadcrumb?.parent) {
        return config.breadcrumb.parent;
      }

      // /post/:id의 경우 from 쿼리파라미터로 결정
      if (config?.path === "/post/:id") {
        const from = searchParams.get('from');
        if (from === 'gallery') {
          return '/post-list/:categoryId';
        }
        if (from === 'portal') {
          return '/portal';
        }
        if (from === 'conference') {
          return '/conference';
        }
        if (from === 'search') {
          return '/search';
        }
        if (from === 'mypage') {
          return '/mypage';
        }
        if (from === 'dedicated') {
          return '/dedicated';
        }
        // [통합] board 쿼리파람 있으면 dedicated에서 온 것 (consultation 통합, from 없는 레거시 케이스)
        const board = searchParams.get('board');
        if (board) {
          return '/dedicated';
        }
        // 기본값 (category에서 온 경우)
        return '/category';
      }

      // [통합] /consultation/:boardId -> /post/:id 통합 (롤백 시 아래 주석 해제)
      // if (config?.path === "/consultation/:boardId") {
      //   const from = searchParams.get('from');
      //   if (from === 'search') {
      //     return '/search';
      //   }
      //   const board = searchParams.get('board');
      //   if (board) {
      //     return '/dedicated';
      //   }
      //   return '/category';
      // }

      // /viewer/:slide의 경우 from 쿼리파라미터로 결정
      if (config?.path === "/viewer/:slide") {
        // 항상 post로 부모 설정 (from 유지되므로 post가 알아서 search 처리)
        return '/post/:id';
      }

      // /write의 경우 from 쿼리파라미터로 결정
      if (config?.path === "/write") {
        const from = searchParams.get('from');
        if (from === 'gallery') {
          return '/post-list/:categoryId';
        }
        if (from === 'category') {
          return '/category';
        }
        if (from === 'portal') {
          return '/portal';
        }
        if (from === 'conference') {
          return '/conference';
        }
        if (from === 'search') {
          return '/search';
        }
        if (from === 'dedicated') {
          return '/dedicated';
        }
        // 다른 경로도 추가 가능
        return undefined;
      }

      return undefined;
    };

    // 2. 부모/조부모 경로 재귀적으로 추가
    const addParentBreadcrumbs = (config: typeof currentConfig) => {
      const parentPath = getDynamicParent(config);
      if (!parentPath) return;

      const parentConfig = routeConfigs.find(r => r.path === parentPath);
      if (!parentConfig?.breadcrumb) return;

      // 조부모가 있으면 먼저 추가 (재귀)
      // 동적 parent도 체크해야 함!
      const grandParentPath = getDynamicParent(parentConfig);
      if (parentConfig.breadcrumb.parent || grandParentPath) {
        addParentBreadcrumbs(parentConfig);
      }

      // /portal 특별 처리: "질환 카테고리" + "카테고리명" 둘 다 표시
      if (parentConfig.path === "/portal") {
        const categoryName = searchParams.get("categoryName");
        const category = searchParams.get("category");
        const subCategoryName = searchParams.get("subCategoryName");

        // 먼저 "질환 카테고리" 추가 (클릭 불가)
        items.push({
          title: (
            <StyledLink to="/portal">
              <span>{parentConfig.breadcrumb.label}</span>
            </StyledLink>
          ),
        });

        // 카테고리명이 있으면 추가 (클릭 가능 - /portal로 이동)
        if (categoryName) {
          items.push({
            title: (
              <StyledLink to={category ? `/portal?category=${category}&categoryName=${encodeURIComponent(categoryName)}` : '/portal'}>
                <span>{safeDecode(categoryName)}</span>
              </StyledLink>
            ),
          });
        }

        // 서브카테고리명이 있으면 추가 (현재 페이지가 /post인 경우)
        if (subCategoryName && config?.path === "/post/:id" && categoryName) {
          items.push({
            title: (
              <StyledLink to={category ? `/portal?category=${category}&categoryName=${encodeURIComponent(categoryName)}&subCategoryName=${encodeURIComponent(subCategoryName)}` : '/portal'}>
                <span>{safeDecode(subCategoryName)}</span>
              </StyledLink>
            ),
          });
        }
      } else if (parentConfig.path === "/conference") {
        // /conference 특별 처리: "소아병리연구회 집담회" + "KPP number" 둘 다 표시
        const kppNumber = searchParams.get("kppNumber");
        const page = searchParams.get("page");

        // 먼저 "소아병리연구회 집담회" 추가 (클릭 가능)
        const conferenceUrl = page ? `/conference?page=${page}` : '/conference';
        items.push({
          title: (
            <StyledLink to={conferenceUrl}>
              <span>{parentConfig.breadcrumb.label}</span>
            </StyledLink>
          ),
        });

        // KPP number가 있으면 추가 (클릭 가능 - conference 페이지로 이동)
        if (kppNumber && config?.path === "/post/:id") {
          const kppParams = new URLSearchParams();
          if (page) kppParams.set('page', page);

          const kppUrl = kppParams.toString() ? `/conference?${kppParams.toString()}` : '/conference';
          items.push({
            title: (
              <StyledLink to={kppUrl}>
                <span>{safeDecode(kppNumber)}</span>
              </StyledLink>
            ),
          });
        }
      } else if (parentConfig.path === "/dedicated") {
        // /dedicated 부모 처리: 게시판명만 표시
        let parentLabel = parentConfig.breadcrumb.label;
        if (parentConfig.breadcrumb.labelFromQuery) {
          const queryLabel = searchParams.get(parentConfig.breadcrumb.labelFromQuery);
          if (queryLabel) {
            parentLabel = getBoardLabel(queryLabel);
          }
        }

        const board = searchParams.get("board");
        items.push({
          title: (
            <StyledLink to={board ? `/dedicated?board=${encodeURIComponent(board)}` : '/dedicated'}>
              <span>{parentLabel}</span>
            </StyledLink>
          ),
        });
      } else if (parentConfig.path === "/post-list/:categoryId") {
        let parentLabel = parentConfig.breadcrumb.label;
        const categoryId = searchParams.get('categoryId');
        const name = searchParams.get('name');

        if (name) {
          parentLabel = safeDecode(name);
        }

        if (categoryId && name) {
          const postListUrl = `/post-list/${categoryId}`;
          items.push({
            title: (
              <StyledLink to={postListUrl}>
                <span>{parentLabel}</span>
              </StyledLink>
            ),
          });
        } else {
          items.push({
            title: (
              <span>{parentLabel}</span>
            ),
          });
        }
      } else {
        // 일반 부모 처리
        let parentLabel = parentConfig.breadcrumb.label;
        const parentIcon = parentConfig.breadcrumb.icon;

        // 부모도 쿼리파라미터에서 동적 라벨 가져오기
        if (parentConfig.breadcrumb.labelFromQuery) {
          // 부모가 /post/:id이고 from=search인 경우 postTitle 사용
          const fromParam = searchParams.get('from');
          const postTitleParam = searchParams.get('postTitle');
          // postId: 쿼리파라미터 우선, 없으면 path의 숫자 세그먼트 (/viewer/2420 등)
          const resolvePostId = () => {
            const fromQuery = searchParams.get('postId');
            if (fromQuery) return fromQuery;
            const segments = location.pathname.split('/').filter(Boolean);
            return segments.find(s => /^\d+$/.test(s)) || null;
          };
          if (parentConfig.path === '/post/:id' && fromParam === 'search' && postTitleParam) {
            const postIdParam = resolvePostId();
            const titleStr = safeDecode(postTitleParam);
            parentLabel = postIdParam ? `${titleStr} (${postIdParam})` : titleStr;
          } else {
            const parentQueryParam = parentConfig.breadcrumb.labelFromQuery;
            const parentQueryLabel = searchParams.get(parentQueryParam);
            if (parentQueryLabel) {
              if (parentConfig.path === '/post/:id') {
                const postIdParam = resolvePostId();
                const titleStr = safeDecode(parentQueryLabel);
                parentLabel = postIdParam ? `${titleStr} (${postIdParam})` : titleStr;
              } else {
                parentLabel = safeDecode(parentQueryLabel);
              }
            }
          }
        }

        // /admin 페이지는 클릭 불가능하게 처리
        if (parentConfig.path === "/admin") {
          items.push({
            title: (
              <span style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                {parentIcon}
                <span>{parentLabel}</span>
              </span>
            ),
          });
          return;
        }

        // useNavigateBack이면 뒤로가기 버튼처럼 동작
        if (config.breadcrumb!.useNavigateBack) {
          if (parentConfig.path === '/search') {
            const title = searchParams.get('title');
            const searchTo = title ? `/search?title=${encodeURIComponent(title)}` : '/search';
            items.push({
              title: (
                <Link to={searchTo}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                    {parentIcon}
                    <span>{parentLabel}</span>
                  </div>
                </Link>
              ),
            });
          } else {
            const isNewTab = window.opener !== null || searchParams.get('newTab') === 'true';
            if (isNewTab && isViewerSlide && parentConfig.path === '/post/:id') {
              const postId = location.pathname.split('/').filter(Boolean).pop();
              const postParams = new URLSearchParams();
              const forwardKeys = ['title', 'from', 'name', 'categoryId', 'board', 'boardName', 'category', 'categoryName', 'subCategoryName', 'kppNumber', 'page', 'postTitle'];
              forwardKeys.forEach(key => {
                const val = searchParams.get(key);
                if (val) postParams.set(key, val);
              });
              const postTo = postId ? `/post/${postId}${postParams.toString() ? `?${postParams.toString()}` : ''}` : '/';
              items.push({
                title: (
                  <Link to={postTo}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                      {parentIcon}
                      <span>{parentLabel}</span>
                    </div>
                  </Link>
                ),
              });
            } else {
              items.push({
                title: (
                  <ClickableText
                    $isViewerSample2Page={isViewerSample2Page}
                    onClick={() => navigate(-1)}
                  >
                    {parentIcon}
                    <span>{parentLabel}</span>
                  </ClickableText>
                ),
              });
            }
          }
        } else {
          // 일반 링크
          items.push({
            title: (
              <Link to={parentConfig.path}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                  {parentIcon}
                  <span>{parentLabel}</span>
                </div>
              </Link>
            ),
          });
        }
      }
    };

    addParentBreadcrumbs(currentConfig);

    // 3. 현재 페이지
    let currentLabel = currentBreadcrumb.label;

    // 쿼리파라미터에서 동적 라벨 가져오기
    if (currentBreadcrumb.labelFromQuery) {
      const fromParam = searchParams.get('from');
      const postTitleParam = searchParams.get('postTitle');

      if (currentConfig.path === "/post/:id") {
        // path에서 postId 추출
        const postId = location.pathname.split('/').pop();
        const titleStr = fromParam === 'search' && postTitleParam
          ? safeDecode(postTitleParam)
          : safeDecode(searchParams.get(currentBreadcrumb.labelFromQuery));
        currentLabel = postId ? `${titleStr} (${postId})` : titleStr;
      } else {
        const queryLabel = searchParams.get(currentBreadcrumb.labelFromQuery);
        const slideIndex = searchParams.get('slideIndex');

        if (currentConfig.path === "/viewer/:slide") {
          // viewer 페이지: #번호 + 슬라이드명
          if (slideIndex) {
            if (queryLabel) {
              currentLabel = `#${slideIndex} ${safeDecode(queryLabel)}`;
            } else {
              currentLabel = `#${slideIndex}`;
            }
          } else if (queryLabel) {
            currentLabel = safeDecode(queryLabel);
          } else {
            currentLabel = "Untitled Slide";
          }
        } else if (queryLabel) {
          currentLabel = currentConfig.path === "/dedicated"
            ? getBoardLabel(queryLabel)
            : safeDecode(queryLabel);
        }
      }
    }

    // /portal 특별 처리: "질환 카테고리" + "카테고리명" 둘 다 표시
    if (currentConfig.path === "/portal") {
      const categoryName = searchParams.get("categoryName");

      // 먼저 "질환 카테고리" 추가
      items.push({
        title: (
          <span>{currentBreadcrumb.label}</span>
        ),
      });

      // 카테고리명이 있으면 추가
      if (categoryName) {
        items.push({
          title: (
            <span>{safeDecode(categoryName)}</span>
          ),
        });
      }
    } else if (currentConfig.path === "/write") {
      // /write 특별 처리: 서브카테고리명 or 게시글제목 + "게시글 등록/수정" 둘 다 표시
      const from = searchParams.get("from");
      const subCategoryName = searchParams.get("subCategoryName");
      const isEdit = searchParams.get("isEdit");
      const postTitle = searchParams.get("postTitle");
      const kppNumber = searchParams.get("kppNumber");

      // gallery 
      if (from === 'gallery') {
        const name = searchParams.get('name');
        const categoryId = searchParams.get('categoryId');

        if (isEdit === 'true' && postTitle) {
          const postId = searchParams.get('postId');

          const backParams = new URLSearchParams();
          backParams.set('title', postTitle);
          backParams.set('from', 'gallery');
          if (name) backParams.set('name', name);
          if (categoryId) backParams.set('categoryId', categoryId);

          const postUrl = postId ? `/post/${postId}?${backParams.toString()}` : '#';
          const displayTitle = postId ? `${safeDecode(postTitle)} (${postId})` : safeDecode(postTitle);

          items.push({
            title: (
              <StyledLink to={postUrl}>
                <span>{displayTitle}</span>
              </StyledLink>
            ),
          });
        }

        const writeLabel = isEdit === 'true' ? '게시글 수정' : currentBreadcrumb.label;
        items.push({
          title: (
            <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
              {currentBreadcrumb.icon}
              <span>{writeLabel}</span>
            </div>
          ),
        });
      } else if (from === 'conference') {
        const page = searchParams.get('page');

        if (kppNumber) {
          const conferenceUrl = page ? `/conference?page=${page}` : '/conference';
          items.push({
            title: (
              <StyledLink to={conferenceUrl}>
                <span>{safeDecode(kppNumber)}</span>
              </StyledLink>
            ),
          });
        }

        if (isEdit === 'true' && postTitle) {
          const postId = searchParams.get('postId');

          const backParams = new URLSearchParams();
          backParams.set('soa', 'true');
          backParams.set('from', 'conference');
          backParams.set('title', postTitle);
          if (kppNumber) backParams.set('kppNumber', kppNumber);
          if (page) backParams.set('page', page);

          // SOA 데이터 추가
          const soaParams = getSoaParams();
          Object.entries(soaParams).forEach(([key, value]) => {
            backParams.set(key, value as string);
          });

          const postUrl = postId ? `/post/${postId}?${backParams.toString()}` : '#';
          const displayTitle = postId ? `${safeDecode(postTitle)} (${postId})` : safeDecode(postTitle);

          items.push({
            title: (
              <StyledLink to={postUrl}>
                <span>{displayTitle}</span>
              </StyledLink>
            ),
          });
        }

        // "게시글 등록" 또는 "게시글 수정" 추가
        const writeLabel = isEdit === 'true' ? '게시글 수정' : currentBreadcrumb.label;
        items.push({
          title: (
            <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
              {currentBreadcrumb.icon}
              <span>{writeLabel}</span>
            </div>
          ),
        });
      } else if (from === 'admin') {
        // 관리자 페이지를 통해 접근항 경우
        const board = searchParams.get('board');
        const boardName = searchParams.get('boardName');
        const main = searchParams.get('main');
        const mainName = searchParams.get('mainName');
        const sub = searchParams.get('sub');
        const subName = searchParams.get('subName');

        const adminCategoryParams = new URLSearchParams();
        if (board) adminCategoryParams.set('board', board);
        if (boardName) adminCategoryParams.set('boardName', boardName);
        if (main) adminCategoryParams.set('main', main);
        if (mainName) adminCategoryParams.set('mainName', mainName);
        if (sub) adminCategoryParams.set('sub', sub);
        if (subName) adminCategoryParams.set('subName', subName);

        items.push({
          title: (
            <StyledLink to={`/admin/category?${adminCategoryParams.toString()}`}>
              <span>Category</span>
            </StyledLink>
          ),
        });

        if (isEdit === 'true' && postTitle) {
          const postId = searchParams.get('postId');

          const backParams = new URLSearchParams();
          backParams.set('from', 'admin');
          backParams.set('title', postTitle);
          if (board) backParams.set('board', board);
          if (boardName) backParams.set('boardName', boardName);
          if (main) backParams.set('main', main);
          if (mainName) backParams.set('mainName', mainName);
          if (sub) backParams.set('sub', sub);
          if (subName) backParams.set('subName', subName);

          const postUrl = postId ? `/post/${postId}?${backParams.toString()}` : '#';
          const displayTitle = postId ? `${safeDecode(postTitle)} (${postId})` : safeDecode(postTitle);

          items.push({
            title: (
              <StyledLink to={postUrl}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                  <FileTextOutlined />
                  <span>{displayTitle}</span>
                </div>
              </StyledLink>
            ),
          });
        }

        // "게시글 등록" 또는 "게시글 수정" 추가
        const writeLabel = isEdit === 'true' ? '게시글 수정' : currentBreadcrumb.label;
        items.push({
          title: (
            <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
              {currentBreadcrumb.icon}
              <span>{writeLabel}</span>
            </div>
          ),
        });
      } else if (from === 'search') {
        const title = searchParams.get('title');

        if (isEdit === 'true' && postTitle) {
          const postId = searchParams.get('postId');

          const backParams = new URLSearchParams();
          backParams.set('from', 'search');
          if (title) backParams.set('title', title);
          backParams.set('postTitle', postTitle);

          const postUrl = postId ? `/post/${postId}?${backParams.toString()}` : '#';
          const displayTitle = postId ? `${safeDecode(postTitle)} (${postId})` : safeDecode(postTitle);

          items.push({
            title: (
              <StyledLink to={postUrl}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                  <FileTextOutlined />
                  <span>{displayTitle}</span>
                </div>
              </StyledLink>
            ),
          });
        }

        const writeLabel = isEdit === 'true' ? '게시글 수정' : currentBreadcrumb.label;
        items.push({
          title: (
            <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
              {currentBreadcrumb.icon}
              <span>{writeLabel}</span>
            </div>
          ),
        });

      } else if (from === 'dedicated') {
        const board = searchParams.get('board');
        const category = searchParams.get('category');
        const categoryName = searchParams.get('categoryName');

        if (categoryName) {
          const dedicatedUrl = board && category
            ? `/dedicated?board=${encodeURIComponent(board)}&category=${category}`
            : board
            ? `/dedicated?board=${encodeURIComponent(board)}`
            : '/dedicated';
          items.push({
            title: (
              <StyledLink to={dedicatedUrl}>
                <span>{safeDecode(categoryName)}</span>
              </StyledLink>
            ),
          });
        }

        if (isEdit === 'true' && postTitle) {
          const postId = searchParams.get('postId');

          const backParams = new URLSearchParams();
          backParams.set('from', 'dedicated');
          if (board) backParams.set('board', board);
          if (category) backParams.set('category', category);
          if (categoryName) backParams.set('categoryName', categoryName);
          backParams.set('title', postTitle);

          const postUrl = postId ? `/post/${postId}?${backParams.toString()}` : '#';
          const displayTitle = postId ? `${safeDecode(postTitle)} (${postId})` : safeDecode(postTitle);

          items.push({
            title: (
              <StyledLink to={postUrl}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                  <FileTextOutlined />
                  <span>{displayTitle}</span>
                </div>
              </StyledLink>
            ),
          });
        }

        const writeLabelDedicated = isEdit === 'true' ? '게시글 수정' : currentBreadcrumb.label;
        items.push({
          title: (
            <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
              {currentBreadcrumb.icon}
              <span>{writeLabelDedicated}</span>
            </div>
          ),
        });

      } else {
        // 일반 모드 (category, portal 등)

        // 서브카테고리명이 있으면 추가 (클릭 가능 - portal 페이지로 이동)
        if (subCategoryName) {
          const category = searchParams.get('category');
          const categoryName = searchParams.get('categoryName');

          items.push({
            title: (
              <StyledLink to={category && categoryName ? `/portal?category=${category}&categoryName=${encodeURIComponent(categoryName)}&subCategoryName=${encodeURIComponent(subCategoryName)}` : '#'}>
                <span>{safeDecode(subCategoryName)}</span>
              </StyledLink>
            ),
          });
        }

        // 수정 모드: 게시글 제목 추가 (클릭 가능 - 포스트 페이지로 이동)
        if (isEdit === 'true' && postTitle) {
          const postId = searchParams.get('postId');
          const from = searchParams.get('from');

          // 포스트 페이지로 돌아가는 URL 구성
          const backParams = new URLSearchParams();
          backParams.set('title', postTitle);
          if (from) backParams.set('from', from);

          // from에 따른 추가 파라미터
          if (from === 'portal') {
            const category = searchParams.get('category');
            const categoryName = searchParams.get('categoryName');
            if (category) backParams.set('category', category);
            if (categoryName) backParams.set('categoryName', categoryName);
            if (subCategoryName) backParams.set('subCategoryName', subCategoryName);
          } else if (from === 'conference') {
            const kppNumber = searchParams.get('kppNumber');
            const page = searchParams.get('page');
            if (kppNumber) backParams.set('kppNumber', kppNumber);
            if (page) backParams.set('page', page);
          }

          const postUrl = postId ? `/post/${postId}?${backParams.toString()}` : '#';
          const displayTitle = postId ? `${safeDecode(postTitle)} (${postId})` : safeDecode(postTitle);

          items.push({
            title: (
              <StyledLink to={postUrl}>
                <span>{displayTitle}</span>
              </StyledLink>
            ),
          });
        }

        // "게시글 등록" 또는 "게시글 수정" 추가
        const writeLabel = isEdit === 'true' ? '게시글 수정' : currentBreadcrumb.label;
        items.push({
          title: (
            <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
              {currentBreadcrumb.icon}
              <span>{writeLabel}</span>
            </div>
          ),
        });
      }
    } else if (currentConfig.path === "/reference") {
      const referenceTitle = searchParams.get("referenceTitle");
      const page = searchParams.get("page");
      const id = searchParams.get("id");

      if(id) {
        items.push({
          title: (
            <StyledLink to={`/reference?page=${page}`}>
              <span>{currentBreadcrumb.label}</span>
            </StyledLink>
          ),
        });
      } else {
        items.push({
          title: (
            <span>{currentBreadcrumb.label}</span>
          ),
        });
      }

      if (referenceTitle) {
        items.push({
          title: (
            <span>{safeDecode(referenceTitle)}</span>
          ),
        });
      }
    } else if (currentConfig.path === "/qna") {
        const qnaTitle = searchParams.get("qnaTitle");
        const page = searchParams.get("page");
        const id = searchParams.get("id");

        if(id) {
          items.push({
            title: (
              <StyledLink to={`/qna?page=${page}`}>
                <span>{currentBreadcrumb.label}</span>
              </StyledLink>
            ),
          });
        } else {
          items.push({
            title: (
              <span>{currentBreadcrumb.label}</span>
            ),
          });
        }

        if (qnaTitle) {
          items.push({
            title: (
              <span>{safeDecode(qnaTitle)}</span>
            ),
          });
        }
    } else if (currentConfig.path === "/dedicated") {
      // /dedicated 페이지: 게시판명 + 카테고리명 표시
      const board = searchParams.get("board");
      const category = searchParams.get("category");

      // 게시판명 (labelFromQuery로 표시됨)
      items.push({
        title: (
          <StyledLink to={board ? `/dedicated?board=${encodeURIComponent(board)}` : '/dedicated'}>
            <span>{currentLabel}</span>
          </StyledLink>
        ),
      });

      // 카테고리명 (category ID로 카테고리명을 가져와야 함)
      // CategoryBoardNoSub에서 activeCategory.name을 URL에 추가하도록 수정 필요
      const categoryName = searchParams.get("categoryName");
      if (categoryName) {
        items.push({
          title: (
            <span>{safeDecode(categoryName)}</span>
          ),
        });
      }
    } else if (currentConfig.path === "/admin/category") {
      const boardName = searchParams.get("boardName");
      const mainName = searchParams.get("mainName");
      const subName = searchParams.get("subName");

      // "카테고리 편집" (클릭x)
      items.push({
        title: (
          <span>{currentBreadcrumb.label}</span>
        ),
      });

      // 게시판명 (클릭x)
      if (boardName) {
        items.push({
          title: (
            <span>{safeDecode(boardName)}</span>
          ),
        });
      }

      // 메인 카테고리명 (클릭x)
      if (mainName) {
        items.push({
          title: (
            <span>{safeDecode(mainName)}</span>
          ),
        });
      }

      // 서브 카테고리명 (클릭x)
      if (subName) {
        items.push({
          title: (
            <span>{safeDecode(subName)}</span>
          ),
        });
      }
    } else if (currentConfig.path === "/post/:id" && searchParams.get("board")) {
      // [통합] dedicated(board 쿼리파람)에서 온 post: 카테고리명 + 포스트 제목 표시
      // (구 /consultation/:boardId 처리 - 롤백 시 위 else if 조건에서 "/consultation/:boardId" 복원)
      const categoryName = searchParams.get("categoryName");
      const category = searchParams.get("category");
      const board = searchParams.get("board");
      const from = searchParams.get("from");

      // 카테고리명
      if (from !== 'search' && categoryName) {
        items.push({
          title: (
            <StyledLink to={category && board ? `/dedicated?board=${encodeURIComponent(board)}&category=${category}` : '#'}>
              <span>{safeDecode(categoryName)}</span>
            </StyledLink>
          ),
        });
      }

      // 포스트 제목
      items.push({
        title: (
          <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            {currentBreadcrumb.icon}
            <span>{currentLabel}</span>
          </div>
        ),
      });
    } else {
      // 일반 페이지
      items.push({
        title: (
          <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            {currentBreadcrumb.icon}
            <span>{currentLabel}</span>
          </div>
        ),
      });
    }

    return items;
  };

  const breadItems = buildBreadcrumbItems();

  return (
    <BreadcrumbWrapper
      $isPostPage={isPostPage}
      $isViewerPage={isViewerPage}
      $isViewerSample2Page={isViewerSample2Page}
    >
      <BreadcrumbStyle
        $isViewerPage={isViewerPage}
        style={{
          padding: "0.2rem 1.2rem",
          backgroundColor: isViewerSample2Page
            ? "#0a1929"
            : (isPostPage ? "#fff" : "transparent"),
        }}
        items={breadItems}
      />
    </BreadcrumbWrapper>
  );
};

const BreadcrumbWrapper = styled.div<{
  $isPostPage: boolean;
  $isViewerPage: boolean;
  $isViewerSample2Page: boolean
}>`
  position: sticky;
  top: ${({ $isPostPage, $isViewerPage }) => ($isPostPage ? "60px" : $isViewerPage && "0")};
  z-index: 100;
  background-color: ${({ $isViewerPage }) => ($isViewerPage ? "#0a1929" : "#fff")};
  border-bottom: 1px solid ${({ $isViewerPage }) => ($isViewerPage ? "#1e3a52" : "#f0f0f0")};
`;

const BreadcrumbStyle = styled(Breadcrumb)<{ $isViewerPage?: boolean }>`
  ol {
    li {
      ${({ $isViewerPage }) => $isViewerPage && `
        color: #fff;

        span {
          color: #fff !important;
        }
      `}

      a {
        transition: none !important;

        ${({ $isViewerPage }) => $isViewerPage && `
          color: #fff !important;

          &:hover {
            color: darkcyan !important;
            text-decoration: underline;
          }
        `}

        ${({ $isViewerPage }) => !$isViewerPage && `
          &:hover {
            color: darkcyan !important;
            background-color: transparent;
            text-decoration: underline;
          }
        `}
      }
    }
  }

  ${({ $isViewerPage }) => $isViewerPage && `
    .ant-breadcrumb-separator {
      color: #8899aa;
    }
  `}
`;

const ClickableText = styled.div<{ $isViewerSample2Page?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  cursor: pointer;
  color: ${({ $isViewerSample2Page }) => ($isViewerSample2Page ? "#fff" : "rgba(0, 0, 0, 0.45)")};

  &:hover {
    color: ${({ $isViewerSample2Page }) => ($isViewerSample2Page ? "#1890ff" : "darkcyan")};
    text-decoration: underline;
  }
`;

const StyledLink = styled(Link)`
  color: rgba(0, 0, 0, 0.45);

  &:hover {
    color: darkcyan;
    text-decoration: underline;
  }
`;

export default CustomBreadcrumb;
